import { AxiosResponse, CancelToken } from 'axios';
import { User } from '../../utils/AccountsAPI';
import { CreatedUpdatedTable } from '../../utils/APIReturnTypes';
import AxiosConfig from '../../utils/AxiosConfig';
import { CostCode } from '../../utils/CYMAAPI';
import { getUTCAmericanDateString, getUTCDateString } from '../../utils/Functions';
import { ShopJobParts } from '../shop/ShopJobsAPI';

const axiosInstance = AxiosConfig.instance();

/*-------------------------------Timesheet-------------------------------------*/
export function getTimesheetById(id: string, token?: CancelToken): Promise<TimeRecordHeader> {
  const url = '/timesheets/' + id;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function getTimesheetForWeekEnding(userId: string, weekEndingDate: string, token?: CancelToken): Promise<TimeRecordHeader> {
  const url = '/accounts/' + userId + '/timesheets/' + weekEndingDate;

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => response.data);
}

export function postTimesheet(timesheet: TimeRecordHeader): Promise<TimeRecordHeader> {
  const url = '/timesheets';

  return axiosInstance.post(url, timesheet).then((response) => response.data);
}

export function putTimesheet(timesheet: TimeRecordHeader): Promise<TimeRecordHeader> {
  const url = '/timesheets/' + timesheet.id;

  return axiosInstance.put(url, timesheet).then((response) => response.data);
}

export function putTimesheetApproval(userId: string, type: 'manager' | 'accounting', approved: boolean): Promise<TimeRecordHeader> {
  const url = `/timesheets/${userId}/approvals/${type}/${approved}`;

  return axiosInstance.put(url).then((response) => response.data);
}

export function getTimesheetsForJob(jobNumber: string, weekEndingDate?: string, token?: CancelToken): Promise<TimeRecordDetail[]> {
  const url = `cyma/jobs/${jobNumber}/timesheets`;

  var config: any = { params: {} };
  if (token !== undefined) {
    config.cancelToken = token;
  }
  if (weekEndingDate !== undefined) config.params.weekEnding = getUTCDateString(weekEndingDate, 'yyyy-mm-dd');

  return axiosInstance.get(url, config).then((response) =>
    (response.data as TimeRecordHeader[]).flatMap((header) => {
      return header.details.map((detail) => {
        detail.header = header;
        return detail;
      });
    })
  );
}

/*-------------------------------Email Notifications-------------------------------*/
export function sendRequestToReopenEmail(
  userName: string,
  date: Date,
  recordType: 'Timesheet' | 'Expense Report' | 'Credit Card Report',
  message?: string,
  token?: CancelToken
) {
  const url = '/accounts/users/' + userName + '/requesttoreopen';

  var config: any = {};
  if (token) {
    config.cancelToken = token;
  }

  return axiosInstance.post(url, { message, date, recordType }, config);
}

export function sendTimesheetReminder(userId: string, token?: CancelToken) {
  const url = '/accounts/users/' + userId + '/timesheetreminder';

  var config: any = {};
  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config);
}

/*-------------------------------Shop Timesheet-------------------------------------*/
export function postShopTimesheet(userId: string, newTransaction: NewTransactionData) {
  const url = `accounts/${userId}/timesheets/shop`;
  return axiosInstance.post(url, newTransaction).then((response) => response.data);
}

/*-------------------------------Export----------------------------------------*/
export function downloadTimesheetExport(
  company: string,
  weekEndingDate: Date | string,
  approvedOnly: boolean,
  unExportedOnly: boolean,
  token?: CancelToken
): Promise<AxiosResponse<File>> {
  const url = '/timesheets/export/' + company + '?weekEndingDate=' + weekEndingDate + (approvedOnly ? '&approved=true' : '') + (unExportedOnly ? '&unexported=true' : '');

  var config: any = {
    responseType: 'blob',
  };

  if (token !== undefined) {
    config.cancelToken = token;
  }

  return axiosInstance.get(url, config).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    var filename =
      response.headers['content-disposition']?.substring(
        response.headers['content-disposition']?.indexOf('=') + 1,
        response.headers['content-disposition']?.indexOf('xlsx') + 4
      ) ?? `timesheetDownload_${getUTCAmericanDateString(weekEndingDate)}.xlsx`;
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    return response;
  });
}

/*-------------------------------Interfaces----------------------------------------*/
export interface NewTransactionData {
  date: string;
  hours: number;
  newStatus?: string;
  notes?: string;
  earningsType?: string;
  shopJobPartsID?: number;
}

export interface TimeRecordHeader extends CreatedUpdatedTable {
  id?: string;
  userId?: string;
  user?: User;
  periodEndingDate?: string | Date;
  managerApprovedBy?: string;
  managerApprovedDate?: string | Date;
  accountingApprovedBy?: string;
  accountingApprovedDate?: string | Date;
  lastExportedDate?: string | Date;
  details: TimeRecordDetail[];
}

export interface TimeRecordDetail extends CreatedUpdatedTable {
  id?: string;
  headerId?: string;
  jobNumber?: string;
  description?: string;
  skill?: string;
  earningsType?: string;
  costCode?: string;
  shopJobPartsID?: number;
  shopJobParts?: ShopJobParts;
  costCodes: CostCode[];
  isPayable?: boolean;
  perDiem?: boolean;
  header?: TimeRecordHeader;
  hours: TimeRecordHours[];
}

export interface TimeRecordHours extends CreatedUpdatedTable {
  id?: string;
  detailId?: string;
  dateOfRecord?: string | Date;
  hours?: number | string;
  detail?: TimeRecordDetail;
}
